import { Call, ApiUrl, ParseGeneralResponse } from "./ApiUtils";

// Load Tendering phase 1 — see docs/superpowers/specs/2026-08-24-load-tendering-design.md

export function fetchAgentTenderPreview(orgId, orderIds) {
  const url = ApiUrl("v2/agent_tender_orders/get_agent_tender_preview");
  return Call(
    "post",
    url,
    { organization_id: orgId, customer_order_ids: orderIds },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        orders: "orders",
        orders_without_agent_warehouse: "orders_without_agent_warehouse",
        already_tendered_orders: "already_tendered_orders",
        missing_order_ids: "missing_order_ids",
        available_agents: "available_agents",
      },
    }
  );
}

// orders: [{ order_id, legs: [{ leg_id, agent_id }] }]
export function saveAgentTender(orgId, orders, { parkDraft = false } = {}) {
  const url = ApiUrl("v2/agent_tender_orders");
  return Call(
    "post",
    url,
    { organization_id: orgId, tender_action: "save", park_draft: parkDraft, orders },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        status: "status",
        batch_id: "batch_id",
        agent_tender_orders: "agent_tender_orders",
        skipped_already_tendered_legs: "skipped_already_tendered_legs",
        agent_emails: "agent_emails",
      },
    }
  );
}

// agents: [{ agent_id, to: [], subject, body }]
// orderIds: optional scope within the batch — a board card is one (order,
// agent) TND, so its send/resend must not touch the batch's sibling orders.
export function sendAgentTender(orgId, batchId, agents, resend = false, orderIds = []) {
  const url = ApiUrl("v2/agent_tender_orders/send_tenders");
  return Call(
    "post",
    url,
    { organization_id: orgId, batch_id: batchId, agents, resend, order_ids: orderIds },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        status: "status",
        batch_id: "batch_id",
        tender_numbers: "tender_numbers",
        emailed_agent_ids: "emailed_agent_ids",
      },
    }
  );
}

// orders: [{ order_id, leg_ids: [] }]; agents carry the edited cancel emails
export function revokeAgentTender(orgId, orders, agents, reason) {
  const url = ApiUrl("v2/agent_tender_orders/revoke");
  return Call(
    "post",
    url,
    { organization_id: orgId, orders, agents, reason },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        status: "status",
        agent_tender_orders: "agent_tender_orders",
        skipped: "skipped",
        agent_emails: "agent_emails",
      },
    }
  );
}

// { kind: 'bol', order_id } or { kind: 'manifest', batch_id, manifest_key }
export function previewAgentTenderDocument(orgId, params) {
  const url = ApiUrl("v2/agent_tender_orders/preview_document");
  return Call("post", url, { organization_id: orgId, ...params }, ParseGeneralResponse, {
    responseDataKeys: { file_name: "file_name", content_type: "content_type", data: "data" },
  });
}

// Board mark-complete / on-hand dialogs (Figma 7082:12688/14390/14527).
// operation: ON_HAND | COMPLETED; updates: [{ order_id, leg_ids: [] }];
// documents: [{ doc_type: 'BOL'|'POP'|'SIGNATURE', file: File }]
export function markAgentTenderLegs(orgId, operation, actualAt, updates, documents = []) {
  const formData = new FormData();
  formData.append("org_id", orgId);
  formData.append("operation", operation);
  formData.append("actual_at", actualAt);
  formData.append("updates", JSON.stringify(updates));
  documents.forEach((doc) => {
    formData.append("documents[][doc_type]", doc.doc_type);
    formData.append("documents[][file]", doc.file, doc.file.name);
  });
  const url = ApiUrl("v2/agent_tender_orders/mark_leg_operation");
  return Call("POST", url, formData, ParseGeneralResponse, {
    responseDataKeys: { updated_legs: "updated_legs" },
  }, false);
}

// Tendered-leg summary for ONE order — fetched lazily by the listing icon popover
export function fetchOrderTenderSummary(orgId, customerOrderId) {
  const url = ApiUrl("v2/agent_tender_orders/get_order_tender_summary");
  return Call("post", url, { organization_id: orgId, customer_order_id: customerOrderId }, ParseGeneralResponse, {
    responseDataKeys: { legs: "legs" },
  });
}

// Tender lifecycle audit trail; filter by batch_id, tender_number, or customer_order_id
export function fetchTenderActivities(orgId, filters = {}) {
  const url = ApiUrl("v2/agent_tender_orders/get_tender_activities");
  return Call("post", url, { organization_id: orgId, ...filters }, ParseGeneralResponse, {
    responseDataKeys: { activities: "activities" },
  });
}

// statuses: e.g. ["SAVED","TENDERED","REVOKED"]; groupBy: agent|leg|terminal|all
export function fetchAgentTenderBoard(orgId, groupBy, statuses, options = {}) {
  const url = ApiUrl("v2/agent_tender_orders/get_agent_tender_orders");
  return Call(
    "post",
    url,
    { organization_id: orgId, group_by: groupBy, statuses, ...options },
    ParseGeneralResponse,
    {
      responseDataKeys: {
        groups: "groups",
        agent_groups: "agent_groups",
        leg_groups: "leg_groups",
        terminal_groups: "terminal_groups",
        available_agents: "available_agents",
        counts: "counts",
        total_cards: "total_cards",
        creators: "creators",
      },
    }
  );
}

// Lightweight order lookup for the tender modal's search-to-add — indexed
// number-prefix match; the orders-listing pipeline made this take seconds.
export function searchTenderOrders(orgId, query) {
  const url = ApiUrl(
    `v2/agent_tender_orders/search_orders?org_id=${orgId}&query=${encodeURIComponent(query)}`
  );
  return Call("get", url, {}, ParseGeneralResponse, {
    responseDataKeys: { orders: "orders" },
  });
}
